import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  IconButton, 
  Box, 
  Typography, 
  Paper,
  Chip,
  DialogActions,
  Button,
  Slide,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Close as CloseIcon, 
  ArrowForwardIos as NextIcon,
  ArrowBackIos as PrevIcon,
  Edit as EditIcon,
  Share as ShareIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Collections as CollectionsIcon
} from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import { Photo } from '../../services/PhotoService';

interface PhotoGalleryPopupProps {
  open: boolean;
  onClose: () => void;
  photos: Photo[];
  initialPhotoIndex: number;
  onEdit?: (photo: Photo) => void;
  onDelete?: (photo: Photo) => void;
  onShare?: (photo: Photo) => void;
  onAddToAlbum?: (photo: Photo) => void;
}

// Slide transition for the dialog
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PhotoGalleryPopup: React.FC<PhotoGalleryPopupProps> = ({
  open,
  onClose,
  photos,
  initialPhotoIndex,
  onEdit,
  onDelete,
  onShare,
  onAddToAlbum
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialPhotoIndex);
  const [showInfo, setShowInfo] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  
  // Reset current index when photos or initialPhotoIndex changes
  useEffect(() => {
    setCurrentIndex(initialPhotoIndex);
  }, [initialPhotoIndex, photos]);
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;
      
      switch (e.key) {
        case 'ArrowRight':
          handleNext();
          break;
        case 'ArrowLeft':
          handlePrev();
          break;
        case 'Escape':
          onClose();
          break;
        case 'i':
          setShowInfo(!showInfo);
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, currentIndex, showInfo]);
  
  // No photos case
  if (!photos || !photos.length) {
     return null;
  }
  
  const currentPhoto = photos[currentIndex];
  
  const handleNext = () => {
    setIsLoading(true);
    setCurrentIndex((prevIndex) => 
      prevIndex === photos.length - 1 ? 0 : prevIndex + 1
    );
  };
  
  const handlePrev = () => {
    setIsLoading(true);
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? photos.length - 1 : prevIndex - 1
    );
  };
  
  const handleImageLoad = () => {
    setIsLoading(false);
  };
  
  // Format date for display
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
     <Dialog
        open={open}
        onClose={onClose}
        fullScreen={fullScreen}
        maxWidth='xl'
        TransitionComponent={Transition}
        PaperProps={{
           sx: {
              bgcolor: 'background.default',
              backgroundImage: 'none',
              height: fullScreen ? '100%' : '90vh',
              width: fullScreen ? '100%' : '90vw',
              position: 'relative',
              overflow: 'hidden',
           },
        }}
     >
        {/* Close button */}
        <IconButton
           aria-label='close'
           onClick={onClose}
           sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.3)',
              zIndex: 1,
              '&:hover': {
                 backgroundColor: 'rgba(0,0,0,0.5)',
              },
           }}
        >
           <CloseIcon />
        </IconButton>

        {/* Info toggle button */}
        <IconButton
           aria-label='toggle info'
           onClick={() => setShowInfo(!showInfo)}
           sx={{
              position: 'absolute',
              right: 16,
              top: 72,
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.3)',
              zIndex: 1,
              '&:hover': {
                 backgroundColor: 'rgba(0,0,0,0.5)',
              },
           }}
        >
           <InfoIcon />
        </IconButton>

        {/* Navigation buttons */}
        <IconButton
           onClick={handlePrev}
           sx={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.3)',
              zIndex: 1,
              '&:hover': {
                 backgroundColor: 'rgba(0,0,0,0.5)',
              },
           }}
        >
           <PrevIcon />
        </IconButton>

        <IconButton
           onClick={handleNext}
           sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.3)',
              zIndex: 1,
              '&:hover': {
                 backgroundColor: 'rgba(0,0,0,0.5)',
              },
           }}
        >
           <NextIcon />
        </IconButton>

        <DialogContent
           sx={{
              p: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: '#000',
              position: 'relative',
              height: '100%',
           }}
        >
           {/* Main image */}
           <img
              src={currentPhoto?.url}
              alt={currentPhoto?.title}
              onLoad={handleImageLoad}
              style={{
                 maxHeight: showInfo ? 'calc(100% - 130px)' : '100%',
                 maxWidth: '100%',
                 objectFit: 'contain',
                 transition: 'opacity 0.3s ease',
                 opacity: isLoading ? 0.5 : 1,
              }}
              onClick={() => setShowInfo(!showInfo)}
           />

           {/* Counter indicator */}
           <Box
              sx={{
                 position: 'absolute',
                 bottom: showInfo ? 130 : 16,
                 left: '50%',
                 transform: 'translateX(-50%)',
                 color: 'white',
                 backgroundColor: 'rgba(0,0,0,0.5)',
                 px: 2,
                 py: 0.5,
                 borderRadius: 2,
                 fontSize: '0.875rem',
              }}
           >
              {currentIndex + 1} / {photos.length}
           </Box>
        </DialogContent>

        {/* Info panel */}
        {showInfo && (
           <Paper
              elevation={3}
              sx={{
                 position: 'absolute',
                 bottom: 0,
                 left: 0,
                 right: 0,
                 p: 2,
                 backgroundColor: 'rgba(255,255,255,0.95)',
                 borderTop: '1px solid',
                 borderColor: 'divider',
                 height: 130,
                 overflow: 'auto',
              }}
           >
              <Box
                 sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
              >
                 <Box>
                    <Typography variant='h6' gutterBottom>
                       {currentPhoto?.title}
                    </Typography>

                    <Typography variant='body2' color='text.secondary' gutterBottom>
                       {currentPhoto?.description || 'No description'}
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                       {currentPhoto?.tags.map((tag) => (
                          <Chip key={tag} label={tag} size='small' variant='outlined' />
                       ))}
                    </Box>

                    <Typography variant='caption' color='text.secondary' display='block'>
                       Taken: {formatDate(currentPhoto?.takenAt)}
                    </Typography>

                    {(currentPhoto?.tripName || currentPhoto?.eventName) && (
                       <Typography variant='caption' color='text.secondary' display='block'>
                          {currentPhoto?.tripName ? `Trip: ${currentPhoto?.tripName}` : ''}
                          {currentPhoto?.tripName && currentPhoto?.eventName ? ' | ' : ''}
                          {currentPhoto?.eventName ? `Event: ${currentPhoto?.eventName}` : ''}
                       </Typography>
                    )}
                 </Box>

                 <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {onAddToAlbum && (
                       <IconButton
                          size='small'
                          color='primary'
                          onClick={() => onAddToAlbum(currentPhoto)}
                          title='Add to event album'
                       >
                          <CollectionsIcon />
                       </IconButton>
                    )}

                    {onEdit && (
                       <IconButton size='small' onClick={() => onEdit(currentPhoto)}>
                          <EditIcon />
                       </IconButton>
                    )}

                    {onShare && (
                       <IconButton
                          size='small'
                          color='primary'
                          onClick={() => onShare(currentPhoto)}
                       >
                          <ShareIcon />
                       </IconButton>
                    )}

                    {onDelete && (
                       <IconButton
                          size='small'
                          color='error'
                          onClick={() => onDelete(currentPhoto)}
                       >
                          <DeleteIcon />
                       </IconButton>
                    )}
                 </Box>
              </Box>
           </Paper>
        )}
     </Dialog>
  );
};

export default PhotoGalleryPopup;
